import { useState, useEffect } from "react";

const useAuthToken = () => {
  const [userToken, setUserToken] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Function to get token from URL parameters or localStorage
    const getToken = (): string | null => {
      // First check URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get("token");

      if (urlToken) {
        // Store token in localStorage for future use (using 'authToken' key to match existing system)

        localStorage.setItem("authToken", urlToken);

        // Verify what was actually stored
        const storedValue = localStorage.getItem("authToken");

        // Clean URL by removing token parameter
        cleanTokenFromUrl();
        return urlToken;
      }

      // Fallback to localStorage (check both 'authToken' and 'token' keys)
      const authToken = localStorage.getItem("authToken");
      const token = localStorage.getItem("token");

      return authToken || token;
    };

    // Function to remove token parameter from URL
    const cleanTokenFromUrl = (): void => {
      const url = new URL(window.location.href);
      if (url.searchParams.has("token")) {
        url.searchParams.delete("token");

        // Update URL without page refresh
        window.history.replaceState({}, document.title, url.toString());
      } else {
        console.log("🧹 No token in URL to clean");
      }
    };

    const token = getToken();

    setUserToken(token);
    setReady(true);
  }, []);

  // Function to get user ID from JWT token
  const getUserIdFromToken = (): string | null => {
    try {
      if (!userToken) return null;

      // Decode JWT token to get user ID
      const payload = JSON.parse(atob(userToken.split(".")[1]));
      return payload.userId || payload.id || payload.sub;
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  };

  // Function to logout (remove token)
  const logout = (): void => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("token"); // Remove both keys
    setUserToken(null);
  };

  return {
    userToken,
    ready,
    setUserToken,
    getUserIdFromToken,
    logout,
    isAuthenticated: !!userToken
  };
};

export default useAuthToken;
